import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, Calendar, Hash } from 'lucide-react';

const TermsOfServicePage = () => {
  return (
    <div className="min-h-screen bg-[#030303] text-white">
      {/* Header */}
      <header className="border-b border-white/5 sticky top-0 z-10 bg-black/80 backdrop-blur-md">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg shadow-purple-500/20">
              <span className="text-white font-bold text-sm">SR</span>
            </div>
            <span className="text-lg font-semibold text-white tracking-tight">
              StackRanked.ai
            </span>
          </Link>
          <div className="flex items-center gap-4 text-sm">
            <span className="flex items-center gap-1 text-gray-400">
              <Hash className="w-4 h-4" />
              Version 1.1
            </span>
            <span className="flex items-center gap-1 text-gray-400">
              <Calendar className="w-4 h-4" />
              Effective: December 15, 2025
            </span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="rounded-xl border border-white/10 p-8 bg-[#0a0a0a]">
          {/* Title */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-lg bg-purple-500/20 flex items-center justify-center">
              <FileText className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Terms of Service</h1>
              <p className="text-sm text-gray-400">StackRanked.ai</p>
            </div>
          </div>

          <div className="prose prose-sm max-w-none">
            <p className="text-sm italic mb-6 text-gray-400">Last Updated: December 15, 2025</p>

            <p className="mb-6 text-gray-300">
              Welcome to StackRanked.ai. StackRanked.ai is a Doing Business As (DBA) name of WyCo Capital LLC. 
              By accessing or using our website and services, you agree to be bound by these Terms of Service. 
              If you do not agree to these terms, please do not use our services.
            </p>

            <h2 className="text-lg font-semibold mt-8 mb-4 text-white">1. Description of Service</h2>
            <p className="mb-4 text-gray-300">
              StackRanked.ai provides a web-based platform that allows teams and individuals to prioritize lists, 
              candidates, features, and ideas using forced-ranking algorithms and collaborative voting tools ("The Service").
            </p>

            <h2 className="text-lg font-semibold mt-8 mb-4 text-white">2. Artificial Intelligence & Machine Learning Disclaimer</h2>
            <p className="mb-4 font-medium text-gray-300">Please read this section carefully.</p>
            <p className="mb-4 text-gray-300">
              StackRanked.ai utilizes Artificial Intelligence (AI) to assist in sorting, analyzing, and processing data. 
              By using The Service, you acknowledge and agree that:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-300">
              <li><strong className="text-white">Probabilistic Nature:</strong> AI models are probabilistic and may produce inconsistent, inaccurate, or unpredictable results.</li>
              <li><strong className="text-white">Risk of "Hallucination":</strong> AI systems are known to occasionally generate content that appears factual but is incorrect, nonsensical, or unrelated to the source input.</li>
              <li><strong className="text-white">No Reliance:</strong> You should not rely solely on the AI-generated rankings or summaries for critical decision-making (including but not limited to hiring, financial investment, or strategic business decisions) without independent human review.</li>
              <li><strong className="text-white">User Responsibility:</strong> You are solely responsible for reviewing and verifying any output generated by StackRanked.ai. We disclaim all liability for any errors, omissions, or inaccuracies in AI-generated content.</li>
            </ul>

            <h2 className="text-lg font-semibold mt-8 mb-4 text-white">3. User Accounts</h2>
            <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-300">
              <li><strong className="text-white">Registration:</strong> To access certain features, you must register for an account. You agree to provide accurate, current, and complete information.</li>
              <li><strong className="text-white">Security:</strong> You are responsible for maintaining the confidentiality of your account credentials. You are responsible for all activities that occur under your account.</li>
              <li><strong className="text-white">Eligibility:</strong> You must be at least 18 years old to use this Service.</li>
            </ul>

            <h2 className="text-lg font-semibold mt-8 mb-4 text-white">4. User Content & Data Privacy</h2>
            <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-300">
              <li><strong className="text-white">Ownership:</strong> You retain full and exclusive ownership of any data, text, or lists you upload or input into StackRanked.ai ("User Content").</li>
              <li><strong className="text-white">Limited License for Operation Only:</strong> By inputting User Content, you grant WyCo Capital LLC a worldwide, non-exclusive, royalty-free license to host, store, and display your content solely as strictly necessary to provide the Service to you.</li>
              <li><strong className="text-white">No Data Usage for Improvements:</strong> We do not use your User Content to train our artificial intelligence models, improve our algorithms, or for any marketing or secondary purposes. Your data remains isolated and is not accessed by our team unless explicitly requested by you for technical support.</li>
              <li><strong className="text-white">Prohibited Content:</strong> You agree not to upload content that is illegal, offensive, infringing on intellectual property rights, or contains sensitive personal data (PII) regarding third parties without their consent.</li>
            </ul>

            <h2 className="text-lg font-semibold mt-8 mb-4 text-white">5. Intellectual Property</h2>
            <p className="mb-4 text-gray-300">
              The StackRanked.ai platform, including its code, design, logos, and algorithms, is the exclusive property of WyCo Capital LLC. 
              You are granted a limited, non-sublicensable license to access and use the Service for your internal business or personal purposes.
            </p>

            <h2 className="text-lg font-semibold mt-8 mb-4 text-white">6. Limitation of Liability</h2>
            <p className="mb-4 text-gray-300">
              To the maximum extent permitted by law, WyCo Capital LLC and its creators shall not be liable for any indirect, 
              incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly 
              or indirectly, or any loss of data, use, goodwill, or other intangible losses, resulting from:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-300">
              <li>Your use or inability to use the Service.</li>
              <li>Any errors or inaccuracies in the AI-generated results or rankings.</li>
              <li>Unauthorized access to or use of our servers and/or any personal information stored therein.</li>
            </ul>

            <h2 className="text-lg font-semibold mt-8 mb-4 text-white">7. Termination</h2>
            <p className="mb-4 text-gray-300">
              We reserve the right to suspend or terminate your access to the Service at our sole discretion, at any time, 
              for any reason, including but not limited to a violation of these Terms.
            </p>

            <h2 className="text-lg font-semibold mt-8 mb-4 text-white">8. Governing Law</h2>
            <p className="mb-4 text-gray-300">
              These Terms shall be governed by and construed in accordance with the laws of the State of Wyoming, 
              without regard to its conflict of law provisions.
            </p>

            <h2 className="text-lg font-semibold mt-8 mb-4 text-white">9. Contact Us</h2>
            <p className="mb-4 text-gray-300">
              If you have any questions about these Terms of Service, please contact us:
            </p>
            <ul className="list-none pl-0 mb-4 space-y-1 text-gray-300">
              <li><strong className="text-white">Entity:</strong> WyCo Capital LLC</li>
              <li><strong className="text-white">Email:</strong> <a href="mailto:support@stackranked.ai" className="text-purple-400 hover:underline">support@stackranked.ai</a></li>
            </ul>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 py-6">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            © {new Date().getFullYear()} WyCo Capital LLC. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="text-sm text-gray-400 hover:text-white transition-colors">Privacy Policy</Link>
            <span className="text-sm font-medium text-white">Terms of Service</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfServicePage;
